/**
 * Контракти адміністрування диска (`/services/disk/admin/...`, `DISK-006`
 * §6.2, ADR 052). Поля — рівно ті, що віддають контролери
 * `backend/web/src/Containers/DiskContainer/UI/API/Controllers/*\/DiskAdmin*`
 * і їхні Action-и (`GetDiskAdmin*Action`, `DiskAdminView`).
 */

/** Тип простору — `StorageType` беку. */
export type DiskAdminStorageType = 'personal' | 'company' | 'department' | 'custom'

/** Стан простору — `StorageState` беку. */
export type DiskAdminStorageState = 'active' | 'frozen' | 'archived' | 'transferred' | 'deleted'

/** Категорія смуги «Чим зайнято» — `DiskUsageCategory` беку, фіксований порядок. */
export type DiskUsageCategory = 'pdf' | 'images' | 'documents' | 'spreadsheets' | 'media' | 'archives' | 'other'

export interface DiskAdminDepartmentRef {
    uuid: string | null
    title: string | null
}

/** Людина в адмін-відповідях (`DiskAdminView::person`). */
export interface DiskAdminPerson {
    uuid: string | null
    name: string | null
    photoUrl?: string | null
    status?: string
    position?: string | null
    departments?: DiskAdminDepartmentRef[]
}

export interface DiskAdminCategory {
    category: DiskUsageCategory
    label: string
    bytes: number
    files: number
}

/** Стан місця: поточні версії, старі версії, кошик, резерв завантажень. */
export interface DiskAdminByState {
    current: number
    oldVersions: number
    trash: number
    reserved: number
}

export interface DiskAdminDuplicates {
    groups: number
    files: number
    bytes: number
}

export interface DiskAdminOffboardingRef {
    uuid: string | null
    status: string
    dueAt: string | null
    overdue: boolean
}

// ---- Огляд --------------------------------------------------------------

export interface DiskAdminTenantTotals {
    used: number
    reserved: number
    /** Ліміт тенанта (`DISK_TENANT_QUOTA_BYTES`); `null` — не задано. */
    quota: number | null
    percent: number | null
    files: number
    folders: number
    versions: number
    /** Сума версій — те, що рахує квота. */
    logical: number
    /** Байти на сервері: усі блоби, зокрема ті, що чекають прибирання. */
    physical: number
    /** Блоби, на які ще посилаються версії. */
    referenced: number
    blobs: number
    pendingGc: number
    pendingGcBlobs: number
    pendingGcDays: number
    dedupSaved: number
    personalQuotaDefault: number
}

export interface DiskAdminStorageTypeRow {
    type: 'personal' | 'company' | 'department' | 'other'
    storages: number
    used: number
    reserved: number
}

export interface DiskAdminTopItem {
    uuid: string
    type: DiskAdminStorageType
    title: string
    state: DiskAdminStorageState
    owner: DiskAdminPerson | null
    used: number
    quota: number | null
    percent: number | null
}

export interface DiskAdminOverview {
    tenant: DiskAdminTenantTotals
    byCategory: DiskAdminCategory[]
    byState: DiskAdminByState
    byStorageType: DiskAdminStorageTypeRow[]
    top: { items: DiskAdminTopItem[]; total: number }
    reclaimable: {
        oldVersions: number
        oldVersionsCount: number
        trashOlderThan: number
        trashOlderThanDays: number
        duplicates: DiskAdminDuplicates
    }
    offboarding: { pending: number; overdue: number }
    generatedAt: string | null
}

// ---- Тренд --------------------------------------------------------------

export const DISK_ADMIN_TREND_DAYS = [30, 90, 365] as const

export type DiskAdminTrendDays = (typeof DISK_ADMIN_TREND_DAYS)[number]

export interface DiskAdminTrendPoint {
    day: string
    used: number
    files: number
    oldVersions: number
    trash: number
    /** Сьогоднішня точка — живий агрегат, не нічний знімок. */
    live?: boolean
}

export interface DiskAdminTrend {
    days: number
    from: string
    to: string
    quota: number | null
    snapshots: number
    points: DiskAdminTrendPoint[]
}

// ---- Список дисків ------------------------------------------------------

/** Чипи-фільтри списку — `DiskAdminListQuery::FILTERS` беку. */
export const DISK_ADMIN_FILTERS = [
    'all',
    'personal',
    'shared',
    'departed',
    'over80',
    'overQuota',
    'inactive',
    'noDisk',
] as const

export type DiskAdminFilter = (typeof DISK_ADMIN_FILTERS)[number]

/** Сортування — білий список беку (`DiskAdminListQuery::SORTS`). */
export const DISK_ADMIN_SORTS = ['used', 'percent', 'files', 'oldVersions', 'trash', 'lastActivity', 'title'] as const

export type DiskAdminSort = (typeof DISK_ADMIN_SORTS)[number]

export type DiskAdminOrder = 'asc' | 'desc'

export interface DiskAdminStorageRow {
    /** `storage:<uuid>` або `user:<uuid>` для співробітника без диска. */
    key: string
    uuid: string | null
    hasDisk: boolean
    type: DiskAdminStorageType
    title: string
    state: DiskAdminStorageState
    departed: boolean
    owner: DiskAdminPerson | null
    department: DiskAdminDepartmentRef | null
    used: number
    reserved: number
    quota: number | null
    quotaIsDefault: boolean
    percent: number | null
    files: number
    oldVersions: number
    trash: number
    lastActivityAt: string | null
    inactive: boolean
    offboarding: DiskAdminOffboardingRef | null
}

export type DiskAdminCounts = Record<DiskAdminFilter, number>

export interface DiskAdminStorageList {
    items: DiskAdminStorageRow[]
    total: number
    counts: DiskAdminCounts
    inactiveDays: number
}

// ---- Картка простору ----------------------------------------------------

export interface DiskAdminStorage {
    uuid: string
    type: DiskAdminStorageType
    title: string
    state: DiskAdminStorageState
    stateLabel: string
    frozenAt: string | null
    owner: DiskAdminPerson | null
    department: DiskAdminDepartmentRef | null
    used: number
    reserved: number
    quota: number | null
    quotaBytes: number | null
    quotaIsDefault: boolean
    defaultQuota: number | null
    percent: number | null
}

export interface DiskAdminFolderRow {
    uuid: string
    name: string
    bytes: number
    currentBytes: number
    files: number
}

export interface DiskAdminRootFile {
    uuid: string
    name: string
    kind: string
    size: number
    updatedAt: string | null
}

export interface DiskAdminSharedGroup {
    rows: number
    files: number
    bytes: number
}

export interface DiskAdminStorageCard {
    storage: DiskAdminStorage
    /** Чи бачить адміністратор склад простору; активний «Мій диск» — ніколи. */
    contentVisible: boolean
    totals: {
        files: number
        folders: number
        versions: number
        current: number
        oldVersions: number
        oldVersionsCount: number
        trash: number
        trashFiles: number
    }
    byCategory: DiskAdminCategory[]
    byState: DiskAdminByState
    physical: { logical: number; physical: number; dedupSaved: number; exclusive: number }
    pendingGc: { scope: string; blobs: number; bytes: number; days: number }
    reclaimable: {
        oldVersions: number
        oldVersionsCount: number
        trash: number
        trashOlderThan: number
        trashOlderThanDays: number
        duplicates: DiskAdminDuplicates
    }
    lastActivityAt: string | null
    lastUploadAt: string | null
    offboarding: DiskAdminOffboardingRef | null
    /** Лише коли `contentVisible`. */
    topFolders?: DiskAdminFolderRow[]
    topFoldersTotal?: number
    rootFiles?: { files: number; bytes: number; items: DiskAdminRootFile[] }
    /** Лише для замороженого диска звільненого: підсумки трьох груп майстра. */
    shared?: { active: DiskAdminSharedGroup; idle: DiskAdminSharedGroup; own: DiskAdminSharedGroup }
    permissions: { quota: boolean; content: boolean }
}

/** Попередження PATCH квоти: квота нижче зайнятого. */
export interface DiskAdminQuotaWarning {
    uuid: string | null
    code: 'below_used'
    used: number
    quota: number
    shortBytes: number
    percent: number | null
    message: string
}

export interface DiskAdminQuotaResult {
    items: DiskAdminStorage[]
    updated: number
    warnings: DiskAdminQuotaWarning[]
}

// ---- Політика -----------------------------------------------------------

export type DiskAfterDeadline = 'remind' | 'transfer' | 'archive' | 'delete'

export interface DiskPolicyValues {
    personalQuotaBytes: number
    versionsToKeep: number
    trashDays: number
    inactiveDays: number
    offboardingDays: number
    reminderDays: number[]
    afterDeadline: DiskAfterDeadline
    sharedActiveDays: number
    suspendPublicLinks: boolean
}

export interface DiskPolicy {
    policy: DiskPolicyValues
    defaults: DiskPolicyValues
    afterDeadlineOptions: { value: DiskAfterDeadline; label: string }[]
    tenantQuotaBytes: number | null
    tenantQuotaSource: string
    isDefault: boolean
    updatedAt: string | null
    updatedBy: { uuid: string | null; name: string | null } | null
}

// ---- Журнал -------------------------------------------------------------

export interface DiskAdminAuditEntry {
    uuid: string
    event: string
    eventLabel: string
    occurredAt: string
    /** `null` — «Система» (дія політики). */
    actor: DiskAdminPerson | null
    storage: { uuid: string | null; type: DiskAdminStorageType; title: string | null } | null
    subject: DiskAdminPerson | null
    payload: Record<string, unknown>
    reason: string | null
}

export interface DiskAdminAuditPage {
    items: DiskAdminAuditEntry[]
    total: number
}

export interface DiskAdminAuditFilter {
    from: string | null
    to: string | null
    actor: string | null
    event: string | null
    storage: string | null
}

// ---- Справи звільнення --------------------------------------------------

export interface DiskOffboardingRow {
    uuid: string
    status: string
    statusLabel: string
    deactivatedAt: string | null
    dueAt: string | null
    overdue: boolean
    daysLeft: number | null
    user: DiskAdminPerson | null
    storage: (DiskAdminStorage & { files: number }) | null
}

// ---- Пороги заповнення --------------------------------------------------

/**
 * Пороги кольору смуги квоти (`DISK-006` §5.3) — ті самі, що в сповіщеннях
 * «простір заповнено» (`DiskNotificationCode::threshold()`); пари тримає
 * вартовий «константи, скопійовані з беку».
 */
export const DISK_ADMIN_QUOTA_WARN_PERCENT = 80
export const DISK_ADMIN_QUOTA_CRITICAL_PERCENT = 95

// ---- Події журналу ------------------------------------------------------

/**
 * Коди подій журналу адміністратора — `DiskAdminAuditEvent` беку (копію
 * тримає вартовий «значення enum беку збігаються з копією на фронті»).
 * Таблиця журналу малює `eventLabel` з бека; підписи нижче — лише для
 * фільтра «Подія».
 */
export const DISK_ADMIN_AUDIT_EVENTS = [
    'storage_viewed',
    'file_opened',
    'quota_changed',
    'policy_changed',
    'cleanup_requested',
    'nodes_trashed',
    'nodes_purged',
    'versions_pruned',
    'trash_emptied',
    'nodes_moved',
    'offboarding_opened',
    'offboarding_decided',
    'offboarding_cancelled',
    'offboarding_reverted',
    'report_exported',
] as const

export type DiskAdminAuditEvent = (typeof DISK_ADMIN_AUDIT_EVENTS)[number]

export const DISK_ADMIN_AUDIT_EVENT_LABELS: Record<DiskAdminAuditEvent, string> = {
    storage_viewed: 'Переглянуто склад диска',
    file_opened: 'Відкрито файл',
    quota_changed: 'Змінено квоту',
    policy_changed: 'Змінено політику диска',
    cleanup_requested: 'Попрошено звільнити місце',
    nodes_trashed: 'Переміщено в кошик',
    nodes_purged: 'Видалено назавжди',
    versions_pruned: 'Видалено старі версії',
    trash_emptied: 'Очищено кошик',
    nodes_moved: 'Переміщено',
    offboarding_opened: 'Відкрито справу звільнення',
    offboarding_decided: 'Виконано рішення по диску звільненого',
    offboarding_cancelled: 'Справу звільнення скасовано',
    offboarding_reverted: 'Передане повернуто',
    report_exported: 'Вивантажено звіт',
}
